import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Mail, MessageSquare, Sparkles, Copy } from "lucide-react";

const CHANNEL_META = {
  sms:   { icon: MessageSquare, label: "SMS",   color: "bg-orange-100 text-[#FF5A00]" },
  email: { icon: Mail,          label: "Email", color: "bg-sky-100 text-sky-700" },
};

export default function CommsTemplatesPanel() {
  const [rows, setRows] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, name, channel, subject, body}
  const [filterChannel, setFilterChannel] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: f }] = await Promise.all([
        api.get("/comms/templates"),
        api.get("/comms/templates/merge-fields"),
      ]);
      setRows(t?.templates || []);
      setFields(f?.fields || []);
    } catch {
      toast.error("Failed to load templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filterChannel === "all") return rows;
    return rows.filter((r) => r.channel === filterChannel);
  }, [rows, filterChannel]);

  const openNew = (channel = "sms") => setEditing({
    name: "", channel, subject: "", body: "",
  });
  const openEdit = (t) => setEditing({ ...t });

  const save = async () => {
    const t = editing;
    if (!t?.name?.trim() || !t?.body?.trim()) {
      toast.error("Name and body are required");
      return;
    }
    try {
      const payload = {
        name: t.name.trim(),
        channel: t.channel,
        subject: t.subject || "",
        body: t.body,
      };
      if (t.id) {
        await api.put(`/comms/templates/${t.id}`, payload);
        toast.success("Template updated");
      } else {
        await api.post(`/comms/templates`, payload);
        toast.success("Template created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await api.delete(`/comms/templates/${t.id}`);
      toast.success("Template deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const duplicate = async (t) => {
    try {
      await api.post(`/comms/templates`, {
        name: `${t.name} (copy)`,
        channel: t.channel,
        subject: t.subject || "",
        body: t.body,
      });
      toast.success("Template duplicated");
      await load();
    } catch { toast.error("Duplicate failed"); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden mb-8" data-testid="comms-templates-panel">
      <div className="p-6 border-b border-zinc-100 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#FF5A00]"/>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold">Message templates</h2>
            <p className="text-sm text-zinc-500">Reusable SMS and email templates with merge fields. Insert these from any composer.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filterChannel} onValueChange={setFilterChannel}>
            <SelectTrigger className="h-9 w-32" data-testid="templates-channel-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              <SelectItem value="sms">SMS only</SelectItem>
              <SelectItem value="email">Email only</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => openNew("sms")} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="templates-new-btn">
            <Plus className="w-4 h-4 mr-1"/> New template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-zinc-500">Loading templates…</div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center text-sm text-zinc-500" data-testid="templates-empty">
          No templates yet. Click <strong>New template</strong> to create one.
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {filtered.map((t) => {
            const M = CHANNEL_META[t.channel] || CHANNEL_META.sms;
            const Icon = M.icon;
            return (
              <div key={t.id} className="p-4 flex items-start justify-between gap-4" data-testid={`template-row-${t.id}`}>
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className={`shrink-0 w-8 h-8 rounded-md ${M.color} flex items-center justify-center`}>
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{t.name}</span>
                      <span className={`text-[9px] font-mono-accent uppercase tracking-wider px-1.5 py-0.5 rounded ${M.color}`}>{M.label}</span>
                      {t.is_seeded && <span className="text-[9px] font-mono-accent uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">Default</span>}
                    </div>
                    {t.channel === "email" && t.subject && (
                      <div className="text-xs text-zinc-500 mt-0.5 truncate"><span className="font-mono-accent uppercase text-[9px] tracking-wider">Subject:</span> {t.subject}</div>
                    )}
                    <div className="text-xs text-zinc-600 mt-1 line-clamp-2 whitespace-pre-wrap">{t.body}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => duplicate(t)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500" title="Duplicate" data-testid={`template-duplicate-${t.id}`}>
                    <Copy className="w-4 h-4"/>
                  </button>
                  <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-orange-50 text-[#FF5A00]" title="Edit" data-testid={`template-edit-${t.id}`}>
                    <Pencil className="w-4 h-4"/>
                  </button>
                  <button onClick={() => remove(t)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Delete" data-testid={`template-delete-${t.id}`}>
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TemplateEditorDialog
        open={!!editing}
        value={editing}
        onChange={setEditing}
        onCancel={() => setEditing(null)}
        onSave={save}
        fields={fields}
      />
    </div>
  );
}


function TemplateEditorDialog({ open, value, onChange, onCancel, onSave, fields }) {
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);
  const [preview, setPreview] = useState(null);

  const refresh = async () => {
    if (!value) return;
    try {
      const { data } = await api.post("/comms/templates/preview", {
        subject: value.subject || "",
        body: value.body || "",
      });
      setPreview(data);
    } catch { setPreview(null); }
  };
  useEffect(() => {
    if (open) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value?.body, value?.subject]);

  if (!open || !value) return null;

  const insertField = (fieldKey, target = "body") => {
    const token = "{" + fieldKey + "}";
    const el = target === "subject" ? subjectRef.current : bodyRef.current;
    if (!el) {
      onChange({ ...value, [target]: (value[target] || "") + token });
      return;
    }
    const start = el.selectionStart ?? (value[target] || "").length;
    const end = el.selectionEnd ?? start;
    const current = value[target] || "";
    const next = current.slice(0, start) + token + current.slice(end);
    onChange({ ...value, [target]: next });
    setTimeout(() => {
      try {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      } catch { /* ignore */ }
    }, 0);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-3xl" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle>{value.id ? "Edit template" : "New template"}</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="md:col-span-3 space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={value.name}
                onChange={(e) => onChange({ ...value, name: e.target.value })}
                placeholder="e.g. Appointment confirmation"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label>Channel</Label>
              <Select value={value.channel} onValueChange={(v) => onChange({ ...value, channel: v })}>
                <SelectTrigger data-testid="template-channel-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {value.channel === "email" && (
              <div>
                <Label>Subject</Label>
                <Input
                  ref={subjectRef}
                  value={value.subject || ""}
                  onChange={(e) => onChange({ ...value, subject: e.target.value })}
                  placeholder="e.g. {company_name}: Your estimate is ready"
                  data-testid="template-subject-input"
                />
              </div>
            )}
            <div>
              <Label>Body</Label>
              <Textarea
                ref={bodyRef}
                rows={value.channel === "email" ? 10 : 6}
                value={value.body}
                onChange={(e) => onChange({ ...value, body: e.target.value })}
                placeholder="Write your message. Click a merge field →"
                className="font-mono-accent text-sm"
                data-testid="template-body-input"
              />
              <p className="text-[11px] text-zinc-500 mt-1">
                Merge fields use <code className="bg-zinc-100 px-1 rounded">{"{customer_first_name}"}</code> style tokens.
                Missing values render as empty.
              </p>
            </div>
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label className="text-xs">Merge fields</Label>
            <div className="border border-zinc-200 rounded-md max-h-[320px] overflow-y-auto bg-zinc-50">
              {(fields || []).map((f) => (
                <div key={f.key} className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-zinc-100 last:border-b-0 hover:bg-white">
                  <div className="min-w-0">
                    <div className="text-xs font-mono-accent text-zinc-900 truncate">{"{" + f.key + "}"}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{f.label}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    {value.channel === "email" && (
                      <button
                        onClick={() => insertField(f.key, "subject")}
                        className="text-[9px] font-mono-accent uppercase tracking-wider px-1.5 py-1 rounded hover:bg-orange-50 text-zinc-600 hover:text-[#FF5A00]"
                        title="Insert into subject"
                        data-testid={`template-insert-subject-${f.key}`}
                      >Subj</button>
                    )}
                    <button
                      onClick={() => insertField(f.key, "body")}
                      className="text-[9px] font-mono-accent uppercase tracking-wider px-1.5 py-1 rounded hover:bg-orange-50 text-zinc-600 hover:text-[#FF5A00]"
                      title="Insert into body"
                      data-testid={`template-insert-body-${f.key}`}
                    >+ Body</button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 rounded-md" data-testid="template-preview">
              <div className="text-[10px] font-mono-accent uppercase tracking-wider text-zinc-500 mb-1">Preview</div>
              {value.channel === "email" && preview?.subject && (
                <div className="text-xs font-medium mb-1">{preview.subject}</div>
              )}
              <div className="text-xs text-zinc-700 whitespace-pre-wrap">
                {preview?.body || "(empty)"}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSave} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="template-save-btn">
            {value.id ? "Save changes" : "Create template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
